import React from 'react';
import { LucideIcon } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  isLoading?: boolean;
  fullWidth?: boolean;
  children?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  isLoading = false,
  fullWidth = false,
  children,
  className = '',
  disabled,
  ...props
}) => {
  const baseStyles = `
    inline-flex items-center justify-center gap-2
    font-medium transition-all duration-200
    disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
    active:scale-[0.98]
  `;

  const variantStyles = {
    primary: `
      bg-gradient-to-b from-[#fff7a8] to-[#fdf751]
      dark:from-[#fdf751] dark:to-[#e6d800]
      text-slate-900 dark:text-gray-900
      shadow-[0_4px_14px_rgba(253,247,81,0.25)]
      hover:shadow-[0_6px_20px_rgba(253,247,81,0.35)]
      hover:-translate-y-0.5
      border-0
    `,
    secondary: `
      bg-white dark:bg-gray-700 text-slate-700 dark:text-slate-200
      border border-slate-200 dark:border-gray-600
      hover:bg-slate-50 dark:hover:bg-gray-600 hover:border-slate-300 dark:hover:border-gray-500
      shadow-sm
    `,
    outline: `
      bg-transparent text-slate-700 dark:text-slate-200
      border border-slate-200 dark:border-gray-600
      hover:bg-slate-50 dark:hover:bg-gray-700 hover:border-slate-300 dark:hover:border-gray-500
    `,
    ghost: `
      bg-transparent text-slate-600 dark:text-slate-300
      hover:bg-slate-100 dark:hover:bg-gray-700 hover:text-slate-900 dark:hover:text-slate-100
    `,
    danger: `
      bg-red-600 text-white
      hover:bg-red-700
      shadow-sm
    `
  };

  // Responsive sizes with touch-friendly dimensions on mobile (min 44px height)
  const sizeStyles = {
    sm: 'h-11 sm:h-8 px-4 sm:px-3 text-sm sm:text-xs rounded-lg min-h-[44px] sm:min-h-0',
    md: 'h-12 sm:h-10 px-5 sm:px-4 text-base sm:text-sm rounded-xl min-h-[44px] sm:min-h-0',
    lg: 'h-14 sm:h-12 px-7 sm:px-6 text-lg sm:text-base rounded-xl min-h-[44px] sm:min-h-0',
    icon: 'h-12 w-12 sm:h-10 sm:w-10 p-0 rounded-xl min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0'
  };

  // Responsive icon sizes
  const iconSizes = {
    sm: 'w-4 h-4 sm:w-3.5 sm:h-3.5',
    md: 'w-5 h-5 sm:w-4 sm:h-4',
    lg: 'w-6 h-6 sm:w-5 sm:h-5',
    icon: 'w-6 h-6 sm:w-5 sm:h-5'
  };

  const Spinner = () => (
    <svg
      className={`animate-spin ${iconSizes[size]}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button
      className={`
        ${baseStyles}
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {Icon && iconPosition === 'left' && size !== 'icon' && (
            <Icon className={iconSizes[size]} />
          )}
          {size === 'icon' && Icon ? (
            <Icon className={iconSizes[size]} />
          ) : (
            children
          )}
          {Icon && iconPosition === 'right' && size !== 'icon' && (
            <Icon className={iconSizes[size]} />
          )}
        </>
      )}
    </button>
  );
};

export default Button;
